import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const STATUS_OPTS = ["Not Started", "In Progress", "Submitted", "Revise", "Passed", "Dropped"];
const READINESS_OPTS = ["Project-ready", "Needs mentoring", "Strong communicator"];
const TRACKS = [
  { slug: "infra_ppp", label: "Infrastructure & PPP" },
  { slug: "career_counsel", label: "Career Counseling" },
];

type Candidate = {
  id: string;
  candidate_name: string;
  contact_phone: string;
  referred_by: string | null;
  track: string;
  project_assigned: string;
  mentor_name: string | null;
  stage1_status: string; stage1_score: number | null;
  stage2_status: string; stage2_score: number | null;
  stage3_status: string; stage3_score: number | null;
  overall_status: string;
  certificate_issued: boolean;
  readiness_tag: string | null;
  notes: string | null;
};

type Project = { id: string; track: string; project_name: string };

const AmbassadorMentor = () => {
  const { user, loading, isAdmin } = useAuth();
  const [rows, setRows] = useState<Candidate[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filterTrack, setFilterTrack] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    candidate_name: "", contact_phone: "", track: "infra_ppp",
    project_assigned: "", referred_by: "",
  });

  const load = async () => {
    const [{ data: cs }, { data: ps }] = await Promise.all([
      (supabase as any).from("ambassador_candidates").select("*").order("created_at", { ascending: false }),
      (supabase as any).from("project_library").select("id, track, project_name").order("project_name"),
    ]);
    if (cs) setRows(cs);
    if (ps) setProjects(ps);
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const filtered = useMemo(() => rows.filter(r =>
    (filterTrack === "all" || r.track === filterTrack) &&
    (filterStatus === "all" || r.overall_status === filterStatus)
  ), [rows, filterTrack, filterStatus]);

  const stats = useMemo(() => {
    const s: Record<string, { total: number; certified: number; inProgress: number; dropped: number }> = {};
    for (const t of TRACKS) s[t.slug] = { total: 0, certified: 0, inProgress: 0, dropped: 0 };
    for (const r of rows) {
      const b = s[r.track] || (s[r.track] = { total: 0, certified: 0, inProgress: 0, dropped: 0 });
      b.total++;
      if (r.overall_status === "Certified") b.certified++;
      else if (r.overall_status === "Dropped") b.dropped++;
      else if (r.overall_status !== "Not Started") b.inProgress++;
    }
    return s;
  }, [rows]);

  const updateField = async (id: string, field: keyof Candidate, value: unknown) => {
    setRows(rs => rs.map(r => r.id === id ? { ...r, [field]: value } as Candidate : r));
    const { error } = await (supabase as any)
      .from("ambassador_candidates")
      .update({ [field]: value })
      .eq("id", id);
    if (error) { toast.error(`Save failed: ${error.message}`); load(); }
    else load(); // refresh overall_status (computed)
  };

  const addCandidate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.candidate_name || !form.contact_phone || !form.project_assigned) {
      toast.error("Name, phone, and project are required.");
      return;
    }
    setBusy(true);
    const { error } = await (supabase as any).from("ambassador_candidates").insert({
      candidate_name: form.candidate_name.trim(),
      contact_phone: form.contact_phone.trim(),
      track: form.track,
      project_assigned: form.project_assigned,
      referred_by: form.referred_by.trim() || null,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Candidate added");
    setForm({ candidate_name: "", contact_phone: "", track: form.track, project_assigned: "", referred_by: "" });
    load();
  };

  if (loading) return <div className="p-8 text-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!isAdmin) return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-16 text-center">
        <h1 className="text-2xl font-bold mb-2">Mentors only</h1>
        <p className="text-muted-foreground">You need mentor/admin access to view this page.</p>
      </main>
      <Footer />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container py-8 space-y-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Ambassador Mentor Dashboard</h1>
          <p className="text-muted-foreground">Track candidates through the 3-stage project journey.</p>
          <p className="text-sm mt-2">
            <a href="/ambassador/toolkit" className="text-primary hover:underline">
              Open Ambassador Toolkit → share templates
            </a>
          </p>
        </div>

        {/* Stats */}
        <div className="grid gap-3 md:grid-cols-2">
          {TRACKS.map(t => {
            const s = stats[t.slug];
            return (
              <div key={t.slug} className="rounded-xl border border-border bg-card p-4">
                <div className="font-semibold mb-2">{t.label}</div>
                <div className="grid grid-cols-4 gap-2 text-center text-sm">
                  <div><div className="text-xl font-bold">{s.total}</div><div className="text-muted-foreground text-xs">Total</div></div>
                  <div><div className="text-xl font-bold text-primary">{s.certified}</div><div className="text-muted-foreground text-xs">Certified</div></div>
                  <div><div className="text-xl font-bold">{s.inProgress}</div><div className="text-muted-foreground text-xs">In Progress</div></div>
                  <div><div className="text-xl font-bold text-destructive">{s.dropped}</div><div className="text-muted-foreground text-xs">Dropped</div></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add candidate */}
        <form onSubmit={addCandidate} className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <h2 className="font-semibold">Add a candidate</h2>
          <div className="grid gap-3 md:grid-cols-5">
            <div>
              <Label>Name</Label>
              <Input value={form.candidate_name} onChange={e => setForm(f => ({ ...f, candidate_name: e.target.value }))} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} />
            </div>
            <div>
              <Label>Track</Label>
              <Select value={form.track} onValueChange={v => setForm(f => ({ ...f, track: v, project_assigned: "" }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TRACKS.map(t => <SelectItem key={t.slug} value={t.slug}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project</Label>
              <Select value={form.project_assigned} onValueChange={v => setForm(f => ({ ...f, project_assigned: v }))}>
                <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {projects.filter(p => p.track === form.track).map(p => (
                    <SelectItem key={p.id} value={p.project_name}>{p.project_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Referred by</Label>
              <Input value={form.referred_by} onChange={e => setForm(f => ({ ...f, referred_by: e.target.value }))} />
            </div>
          </div>
          <Button type="submit" disabled={busy}>{busy ? "Saving…" : "Add candidate"}</Button>
        </form>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs">Track</Label>
            <Select value={filterTrack} onValueChange={setFilterTrack}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tracks</SelectItem>
                {TRACKS.map(t => <SelectItem key={t.slug} value={t.slug}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Overall status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["all", "Not Started", "Stage 1", "Stage 2", "Stage 3", "Certified", "Dropped"].map(s =>
                  <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-3">Candidate</th>
                <th className="p-3">Track</th>
                <th className="p-3">Project</th>
                <th className="p-3">Referred by</th>
                <th className="p-3">Stage 1</th>
                <th className="p-3">Stage 2</th>
                <th className="p-3">Stage 3</th>
                <th className="p-3">Overall</th>
                <th className="p-3">Cert.</th>
                <th className="p-3">Readiness</th>
                <th className="p-3">Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-t border-border align-top">
                  <td className="p-3">
                    <div className="font-medium">{r.candidate_name}</div>
                    <div className="text-xs text-muted-foreground">{r.contact_phone}</div>
                  </td>
                  <td className="p-3 whitespace-nowrap">{TRACKS.find(t => t.slug === r.track)?.label || r.track}</td>
                  <td className="p-3">{r.project_assigned}</td>
                  <td className="p-3">{r.referred_by || "—"}</td>
                  {(["stage1", "stage2", "stage3"] as const).map(s => (
                    <td key={s} className="p-3 space-y-1 min-w-[150px]">
                      <Select
                        value={r[`${s}_status` as keyof Candidate] as string}
                        onValueChange={v => updateField(r.id, `${s}_status` as keyof Candidate, v)}
                      >
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS_OPTS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input
                        type="number" step="0.1" min="1" max="5"
                        placeholder="Score"
                        className="h-8 text-xs"
                        defaultValue={r[`${s}_score` as keyof Candidate] as number ?? ""}
                        onBlur={e => {
                          const raw = e.target.value;
                          const val = raw === "" ? null : Number(raw);
                          if (val !== null && (isNaN(val) || val < 1 || val > 5)) {
                            toast.error("Score must be 1.0 – 5.0"); return;
                          }
                          if (val !== (r[`${s}_score` as keyof Candidate] ?? null))
                            updateField(r.id, `${s}_score` as keyof Candidate, val);
                        }}
                      />
                    </td>
                  ))}
                  <td className="p-3"><Badge variant="secondary">{r.overall_status}</Badge></td>
                  <td className="p-3 text-center">
                    <Checkbox
                      checked={r.certificate_issued}
                      onCheckedChange={v => updateField(r.id, "certificate_issued", !!v)}
                    />
                  </td>
                  <td className="p-3 min-w-[160px]">
                    <Select
                      value={r.readiness_tag ?? ""}
                      onValueChange={v => updateField(r.id, "readiness_tag", v || null)}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{READINESS_OPTS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 min-w-[220px]">
                    <Textarea
                      rows={2}
                      defaultValue={r.notes ?? ""}
                      onBlur={e => { if (e.target.value !== (r.notes ?? "")) updateField(r.id, "notes", e.target.value || null); }}
                    />
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="p-8 text-center text-muted-foreground">No candidates yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AmbassadorMentor;